import React, { memo, useCallback, useEffect, useState } from 'react';
import { Position, useEdges, useReactFlow } from 'reactflow';

import { WordCount } from './AiTextGenerator';
import { NodeProps } from '../../util/Types/NodeProps';
import jsPDF from 'jspdf';
import DataVisualizationModal from '../DataVisualizationModal';
import { Button, Select, MenuItem, Box, Typography } from '@mui/material';
import TagInput from '../Test/TagInput';
import InputHandle from '../UI/InputHandle/InputHandle';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import Node from '../UI/Node/Node';
import { arrayDataToText } from '../../util/dataToText';
import { isActiveEdge } from '../../util/findActiveHandle';
import { useAuthStore } from '../../store/storeAuth';

function OutputControllerNode({ data, type, id }: NodeProps) {
  const [outputText, setOutputText] = useState(data?.text);
  const [savingType, setSavingType] = useState('Save as .txt');
  const fileName = data?.label || 'label';

  const [showDataModal, setShowDataModal] = useState(false);
  const [resData, setResData] = useState('');

  const edges: any = useEdges();

  useEffect(() => {
    if (data?.textData && data.textData.length > 0) {
      setOutputText(data.textData[0].text || '');
      const responseData = data.textData[0].text || '';
      const sanitizedDataString = responseData.replace(/'/g, '"');
      setResData(sanitizedDataString);
    }
  }, [data]);

  const saveTypes = [
    { name: 'Save as .txt', extension: '.txt', mimeType: 'text/plain' },

    { name: 'Save as .html', extension: '.html', mimeType: 'text/html' },
    { name: 'Save as .json', extension: '.json', mimeType: 'application/json' },
    { name: 'Save as .csv', extension: '.csv', mimeType: 'text/csv' },
    { name: 'Save as .xml', extension: '.xml', mimeType: 'application/xml' },
    { name: 'Save as .yaml', extension: '.yaml', mimeType: 'text/yaml' },
  ];
  const onChange = useCallback(
    (e: { target: { value: React.SetStateAction<string> } }) => {
      setSavingType(e.target.value);
    },
    [setSavingType],
  );

  const onDownload = useCallback(
    (e: any) => {
      const element = document.createElement('a');

      const findType = saveTypes.find(type => type.name === savingType);
      if (!findType) {
        return;
      }

      if (findType.extension === '.pdf') {
        const doc = new jsPDF();

        doc.text(outputText, 10, 10);

        doc.save(`${fileName ? fileName : 'output'}.pdf`);

        return;
      }
      const file = new Blob([outputText], {
        type: findType.mimeType,
      });

      element.href = URL.createObjectURL(file);
      element.download = `${fileName ? fileName : 'output'}${
        findType.extension
      }`;

      document.body.appendChild(element); // Required for this to work in FireFox
      element.click();
    },
    [saveTypes, outputText, fileName, savingType],
  );

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  const { user } = useAuthStore(state => state);

  return (
    <Node id={id} data={data} isLoading={false} type={type}>
      <InputHandle
        activeColor={findColor?.color}
        handleId={'output'}
        isActive={isActiveEdge(edges, id, 'output', 'source')}
        right={-12}
        top={'50%'}
        position={Position.Right}
        type="source"
      />

      {!data?.collapsed && (
        <Box p="16px">
          <Box>
            <br />

            <TagInput
              value={
                data?.['textData']?.length > 0
                  ? arrayDataToText(data?.['textData'])
                  : outputText
              }
              isCollapsed={true}
              nodeId={id}
              handleId="input"
              labelName="Output"
              readOnly={true}
              findColor={findColor}
              nodeLabel={data?.label}
              isActive={isActiveEdge(edges, id, 'input', 'target')}
            />
            <WordCount text={arrayDataToText(data?.['textData'] || [])} />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={2}
          >
            <Button variant="contained" onClick={onDownload}>
              Download
            </Button>
            <Select
              onChange={onChange}
              value={savingType}
              name="saveTypes"
              className="nodrag"
              sx={{
                height: 'unset',
                '& .MuiList-root': {
                  backgroundColor:
                    user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                },
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                border: `2px solid ${
                  user?.theme === 'dark' ? '#475467' : '#EAECF0'
                }`,
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .Mui-selected': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                    },
                    backgroundColor:
                      user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  },
                },
                style: {
                  maxHeight: '700px',
                },
              }}
            >
              {saveTypes.map((saveType, index) => (
                <MenuItem key={index} value={saveType.name}>
                  <Typography
                    sx={{
                      color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                    }}
                  >
                    {saveType.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      )}

      <Box>
        <DataVisualizationModal
          resData={resData}
          showDataModal={showDataModal}
          setShowDataModal={setShowDataModal}
        />
      </Box>
    </Node>
  );
}

export default memo(OutputControllerNode);
